import { CommonModule } from '@angular/common';
import {
  Component,
  inject,
  Injector,
  Input,
  ViewChild,
  ViewContainerRef,
  AfterViewInit,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { DynamicField, FieldVariant, FileFieldProps, FileRepresentation, SearchableSelectOption, SelectOption } from '../form-builder.types';
import { DatePickerModule } from 'primeng/datepicker';
import { PasswordModule } from 'primeng/password';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { FileUploadModule } from 'primeng/fileupload';
import { EditorModule } from 'primeng/editor';
import { AccordionModule } from 'primeng/accordion';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TextGroupsComponent } from '../../shared/text-groups/text-groups.component';
import { FileUploaderComponent } from '../../shared/file-uploader/file-uploader.component';
import { isObservable, map, Observable, of, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MultiSelectModule } from 'primeng/multiselect';
import { ColorPickerModule } from 'primeng/colorpicker';
import { RadioButton } from 'primeng/radiobutton';
import { Button } from 'primeng/button';
import { TableModule } from 'primeng/table';

@Component({
  selector: 'sm-field-builder',
  templateUrl: './field-builder.component.html',
  styleUrls: ['./field-builder.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    DatePickerModule,
    PasswordModule,
    InputNumberModule,
    TextareaModule,
    SelectModule,
    MultiSelectModule,
    FileUploadModule,
    EditorModule,
    AccordionModule,
    FormsModule,
    CheckboxModule,
    ToggleSwitchModule,
    TextGroupsComponent,
    FileUploaderComponent,
    TranslateModule,
    ColorPickerModule,
    RadioButton,
    Button,
    TableModule,
  ],
})
export class FieldBuilderComponent implements OnInit, AfterViewInit {
  private _field!: DynamicField<any>;

  @Input()
  set field(value: DynamicField<any>) {
    this._field = value;
    if (value) {
      this.initField();
    }
  }

  get field(): DynamicField<any> {
    return this._field;
  }
  @Output() fieldBlur = new EventEmitter<void>();
  injector = inject(Injector);
  private translate = inject(TranslateService);

  selectedValue?: SelectOption;

  @ViewChild('customComponentContainer', { read: ViewContainerRef, static: false })
  viewContainerRef!: ViewContainerRef;

  options$: Observable<SelectOption[]> = of([]);

  private destroy$ = new Subject<void>();
  private fieldDestroy$ = new Subject<void>();
  private hasSearchable = false;

  ngOnInit() {
    this.initField();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.fieldDestroy$.next();
    this.fieldDestroy$.complete();
  }

  private initField() {
    this.fieldDestroy$.next(); // Clean up previous field subscriptions
    if (!this.field) return;

    const rawOptions = this.field.props?.['options'];

    const options$ = isObservable(rawOptions) ? rawOptions : of(rawOptions ?? []);

    this.options$ = options$.pipe(
      map(options =>
        options
          .filter((opt: SelectOption) => !!opt?.name) // HARD GUARD
          .map((opt: SelectOption) => ({
            ...opt,
            name: this.translate.instant(opt.name as string),
          }))
      )
    );

    this.files =
      this.field?.variant == FieldVariant.FILE
        ? (this.field.props as FileFieldProps).files || of([] as FileRepresentation[])
        : of([] as FileRepresentation[]);

    this.initSearchableDetection();
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterViewInit() {
    if (this.field?.props?.['component']) {
      const componentType = this.field.props['component'];

      const componentRef = this.viewContainerRef.createComponent(componentType, {
        injector: this.injector,
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { component, injectKeys, ...inputs } = this.field.props;

      for (const [key, value] of Object.entries(inputs)) {
        (componentRef.instance as any)[key] = value;
      }

      componentRef.changeDetectorRef.detectChanges();
    }
  }

  allowOnlyPhoneCharacters(event: KeyboardEvent) {
    const allowedChars = /[\d\\+\\-\\#\\*]/;
    if (!allowedChars.test(event.key)) {
      event.preventDefault();
    }
  }

  normalizeError(error: string | string[] | null | undefined): string[] {
    if (!error) return [];
    return Array.isArray(error) ? error : [error];
  }

  private initSearchableDetection(): void {
    const options: SearchableSelectOption[] | Observable<SearchableSelectOption[]> = this.field?.props?.['options'];

    if (!options || !isObservable(options)) {
      this.hasSearchable = Array.isArray(options) ? options.some((o: any) => !!o?.searchable) : false;
      return;
    }

    options.pipe(takeUntil(this.fieldDestroy$)).subscribe((opts: SearchableSelectOption[]) => {
      this.hasSearchable = opts?.some(o => !!o?.searchable) ?? false;
    });
  }

  containsSearchableOption(): boolean {
    return this.hasSearchable;
  }

  files: Observable<FileRepresentation[]> = of([] as FileRepresentation[]);

  onChangeHandler(field: any, event: any) {
    if (field.props && typeof field.props['onChange'] === 'function') {
      field.props['onChange'](event);
    }
  }
}
